// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Locale } from 'jsw';
import apolloClient from 'apolloClient';
import { readGraphQlListInputFromStorage } from 'components/common/GraphQlList';
import TRAFFIC_USAGE_INTERVALS from 'queries/TrafficUsageIntervals.graphql';
import TRAFFIC_USAGE_BY_VIEWER_CLIENTS_LIST from 'queries/TrafficUsageByViewerClientsList.graphql';
import TOTAL_TRAFFIC_USAGE_BY_VIEWER_CLIENTS from 'queries/TotalTrafficUsageByViewerClients.graphql';
import { trafficUsageByResellerRoute } from 'routes/trafficUsageByResellerRoute';

export const trafficUsageByViewerResellersChildrenRoute = ({ path = 'traffic-usage-resellers' } = {}) => ({
    path,
    handle: {
        crumb: () => ({
            title: Locale.getSection('components.routes').lmsg('trafficUsageByResellers'),
        }),
    },
    children: [
        trafficUsageByResellerRoute(
            ':resellerId',
            { clientIdParamName: 'resellerId' },
        ),
    ],
});

export const trafficUsageByViewerResellersRoute = ({ path = 'traffic-usage-resellers', ignoreTopLevelTitle = false } = {}) => ({
    path,
    handle: {
        crumb: () => ignoreTopLevelTitle ? [] : ({
            title: Locale.getSection('components.routes').lmsg('trafficUsageByResellers'),
        }),
        helpContext: 'reseller.traffic',
    },
    children: [
        {
            index: true,
            componentLoader: () => import(
                /* webpackChunkName: "components/pages/TrafficUsageByClientsPage" */
                'components/pages/TrafficUsageByClientsPage'
            ),
            loader: async () => {
                const trafficUsageListKey = 'TrafficUsageByViewerResellersList';
                const clientType = 'reseller';

                const [{ data: intervalsData }] = await Promise.all([
                    apolloClient.query({
                        query: TRAFFIC_USAGE_INTERVALS,
                    }),
                    apolloClient.query({
                        query: TOTAL_TRAFFIC_USAGE_BY_VIEWER_CLIENTS,
                        variables: { clientType },
                    }),
                    apolloClient.query({
                        query: TRAFFIC_USAGE_BY_VIEWER_CLIENTS_LIST,
                        variables: {
                            input: {
                                ...readGraphQlListInputFromStorage({ key: trafficUsageListKey }),
                                clientTypes: [clientType],
                            },
                        },
                    }),
                ]);

                const { trafficUsageIntervals } = intervalsData.viewer.client.statistics;

                return {
                    trafficUsageIntervals,
                    trafficUsageListKey,
                    clientType,
                    getClientLink: clientId => `./${clientId}`,
                };
            },
        },
    ],
});
